import { FormEvent, useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { LocationRecord, locationsApi } from "@/lib/catalog-api";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/catalog/Skeleton";

export default function Locations() {
  const [list, setList] = useState<LocationRecord[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() { setList(await locationsApi.list()); }
  useEffect(() => { load(); }, []);

  async function archive(loc: LocationRecord) {
    setError(null);
    if (loc.item_count > 0) {
      // Frontend-side guard for instant feedback (and a workaround for
      // session-recording interceptors that occasionally drain error bodies
      // before we can read them). Backend enforces the same rule.
      setError(
        `Cannot archive. ${loc.item_count} item${loc.item_count === 1 ? "" : "s"} are currently here. Move them first, then archive.`
      );
      return;
    }
    if (!window.confirm(`Archive ${loc.name}?`)) return;
    try { await locationsApi.archive(loc._id); load(); }
    catch (e) {
      const err = e as ApiError;
      if (err.status === 409) {
        const detail = err.detail as { message?: string; item_count?: number } | null;
        const count = detail?.item_count;
        setError(
          detail?.message ||
            (typeof count === "number"
              ? `Cannot archive. ${count} item${count === 1 ? "" : "s"} are currently here. Move them first, then archive.`
              : "Cannot archive — this location still has items. Move them first.")
        );
      } else {
        setError(err?.message || "Couldn't archive.");
      }
    }
  }

  return (
    <main className="flex-1 px-6 md:px-10 py-10" data-testid="locations-page">
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-2">— Locations</p>
          <h1 className="font-display text-3xl sm:text-4xl">Locations</h1>
        </div>
        <Button data-testid="locations-add-btn" onClick={() => setAdding(true)}
          className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-10 px-5">
          <Plus className="w-4 h-4 mr-2" />New location
        </Button>
      </div>

      {error && <p className="text-sm text-oxblood border border-oxblood/30 bg-oxblood/5 px-3 py-2 mb-4" data-testid="locations-error">{error}</p>}

      {list === null ? (
        <Skeleton className="h-64 max-w-3xl" />
      ) : list.length === 0 ? (
        <div className="border border-border bg-bone/30 p-10 text-center max-w-2xl" data-testid="locations-empty">
          <p className="font-display text-xl">No locations yet.</p>
          <p className="text-sm text-muted-foreground mt-2">Add the racks, bins, archives, and residences where pieces actually live.</p>
        </div>
      ) : (
        <div className="border border-border max-w-4xl" data-testid="locations-table">
          <div className="grid grid-cols-[2fr_80px_120px] text-[10px] uppercase tracking-[0.22em] text-muted-foreground bg-bone/40 px-4 py-2.5 border-b border-border">
            <span>Name</span><span className="text-right">Items</span><span></span>
          </div>
          {list.map((loc) => (
            <div key={loc._id} data-testid={`location-row-${loc._id}`}
              className="grid grid-cols-[2fr_80px_120px] items-center px-4 py-3 border-b border-border last:border-b-0 hover:bg-bone/30 transition-colors">
              <div>
                <p className="font-medium">{loc.name}</p>
                {loc.address && <p className="text-xs text-muted-foreground mt-0.5">{loc.address}</p>}
              </div>
              <span className="text-sm text-right">{loc.item_count}</span>
              <button onClick={() => archive(loc)}
                data-testid={`location-archive-${loc._id}`}
                className="text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-oxblood text-right">
                Archive
              </button>
            </div>
          ))}
        </div>
      )}

      {adding && <LocationNewDialog onClose={() => setAdding(false)} onCreated={() => { setAdding(false); load(); }} />}
    </main>
  );
}

function LocationNewDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      await locationsApi.create({
        name: name.trim(),
        address: address.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      onCreated();
    } catch (e: any) { setError(e?.message || "Could not create."); }
    finally { setBusy(false); }
  }
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-charcoal/40 backdrop-blur-sm px-6" data-testid="location-new-dialog">
      <form onSubmit={submit} className="bg-background border border-border max-w-md w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-[0.22em] text-oxblood">— New location</p>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close"><X className="w-4 h-4" /></button>
        </div>
        <h3 className="font-display text-2xl">Add a location</h3>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input data-testid="location-new-name" value={name} onChange={(e: any) => setName(e.target.value)} required /></div>
          <div><Label>Address / details</Label><Input data-testid="location-new-address" value={address} onChange={(e: any) => setAddress(e.target.value)} /></div>
          <div><Label>Notes</Label><textarea data-testid="location-new-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border border-border bg-background p-3 text-sm" /></div>
        </div>
        {error && <p className="text-sm text-oxblood">{error}</p>}
        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={!name.trim() || busy} data-testid="location-new-submit"
            className="flex-1 bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-11">
            {busy ? "Creating…" : "Create location"}
          </Button>
          <Button type="button" onClick={onClose} className="bg-background border border-border hover:bg-bone/60 rounded-none h-11">Cancel</Button>
        </div>
      </form>
    </div>
  );
}
